import React from "react";
import styled from "styled-components";
import CachedIcon from "@material-ui/icons/Cached";
import { capitalize, head, range } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { TaskSubjectType } from "core/queries/GetTask";
import {
  dayOfWeekOptions,
  intervalUnitOptions,
} from "tasks/components/TaskRecurrence";
import {
  DaysOfWeek,
  RecurrenceIntervalUnit,
  TaskRecurrenceSettingsInput,
} from "typings/globalGQLTypes";

// Fragments
const StyledDropdown = styled(Dropdown)`
  margin-bottom: 1rem;
  margin-right: 0.714rem;
`;

const StyledDropdownButton = styled(Button)`
  border-radius: 0;
  border: none;
  justify-content: left;
  min-height: 2.3rem;
  min-width: 3.5rem;
`;

const StyledMainDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  padding: 1rem;
`;

const StyledDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  max-height: 20rem;
  overflow-y: auto;
`;

const StyledLabel = styled.div`
  color: ${colors.gray6};
  font-size: 0.857rem;
  font-weight: bold;
  margin-bottom: 0.714rem;
`;

interface StandaloneTaskRecurrenceDropdownProps {
  alignContent?: DropdownContentAlignment;
  className?: string;
  disabled?: boolean;
  eventName: string;
  onChange(nextSettings: TaskRecurrenceSettingsInput | null): void;
  recurrenceSetting: TaskRecurrenceSettingsInput | null;
  subjectType?: TaskSubjectType;
  taskId: string;
}

export const StandaloneTaskRecurrenceDropdown = ({
  alignContent,
  className,
  disabled,
  eventName,
  onChange,
  recurrenceSetting,
  taskId,
}: StandaloneTaskRecurrenceDropdownProps) => {
  const { track } = useSegment();

  const onDayOfWeekClick = (daysOfWeek: DaysOfWeek[]) => {
    if (!recurrenceSetting) return null;
    return onChange({ ...recurrenceSetting, daysOfWeek });
  };

  const onIntervalAmountClick = (intervalAmt: number) => {
    if (!recurrenceSetting) return null;
    return onChange({ ...recurrenceSetting, intervalAmt });
  };

  const onIntervalUnitClick = (intervalUnit: RecurrenceIntervalUnit) => {
    if (!recurrenceSetting) return null;
    const isWeekdayRecurring = intervalUnit === RecurrenceIntervalUnit.week;
    return onChange({
      ...recurrenceSetting,
      intervalUnit,
      daysOfWeek: isWeekdayRecurring ? recurrenceSetting.daysOfWeek : [],
    });
  };

  const { intervalAmt, intervalUnit, daysOfWeek } = recurrenceSetting || {};
  const dayOfWeek = head(daysOfWeek);

  return (
    <Dropdown
      className={className}
      trigger={
        <Button
          iconLeft
          disabled={disabled}
          onClick={() =>
            onChange(
              recurrenceSetting || {
                intervalAmt: 1,
                intervalUnit: RecurrenceIntervalUnit.week,
                daysOfWeek: [],
              },
            )
          }
        >
          <CachedIcon />
          {recurrenceSetting
            ? `${intervalAmt} ${capitalize(intervalUnit || undefined)} on ${
                dayOfWeek ? capitalize(dayOfWeek) : "Completion"
              }`
            : "Add Repeat"}
        </Button>
      }
      onClose={() => {
        track({
          eventName,
          action: "click",
          element: "standalone_recurrence_settings.dropdown_trigger",
          props: { nextValue: "closed", taskId },
        });
      }}
      onOpen={() =>
        track({
          eventName,
          action: "click",
          element: "standalone_recurrence_settings.dropdown_trigger",
          props: { nextValue: "opened", taskId },
        })
      }
    >
      {({ isOpen: isMainOpen, closePortal: closeMainPortal }) => {
        return (
          isMainOpen && (
            <StyledMainDropdownContent
              align={alignContent}
              data-testid="standalone-task-recurrence-settings"
            >
              <StyledLabel>Repeats Every:</StyledLabel>
              <InputGroup>
                <StyledDropdown
                  trigger={
                    <Button iconRight>
                      {intervalAmt}
                      <ChevronDown />
                    </Button>
                  }
                >
                  {({ isOpen, closePortal }) =>
                    isOpen && (
                      <StyledDropdownContent>
                        {range(1, 31).map(value => (
                          <StyledDropdownButton
                            key={value}
                            onClick={() => {
                              requestAnimationFrame(() => {
                                closePortal();
                                onIntervalAmountClick(value);
                              });
                            }}
                          >
                            {value}
                          </StyledDropdownButton>
                        ))}
                      </StyledDropdownContent>
                    )
                  }
                </StyledDropdown>
                <StyledDropdown
                  trigger={
                    <Button iconRight>
                      {capitalize(intervalUnit || undefined)}
                      <ChevronDown />
                    </Button>
                  }
                >
                  {({ isOpen, closePortal }) =>
                    isOpen && (
                      <StyledDropdownContent>
                        {intervalUnitOptions.map(option => (
                          <StyledDropdownButton
                            key={option.label}
                            onClick={() => {
                              requestAnimationFrame(() => {
                                closePortal();
                                onIntervalUnitClick(option.value);
                              });
                            }}
                          >
                            {option.label}
                          </StyledDropdownButton>
                        ))}
                      </StyledDropdownContent>
                    )
                  }
                </StyledDropdown>
              </InputGroup>
              {intervalUnit === RecurrenceIntervalUnit.week && (
                <>
                  <StyledLabel>Repeats On:</StyledLabel>
                  <StyledDropdown
                    trigger={
                      <Button iconRight>
                        {dayOfWeek ? capitalize(dayOfWeek) : "Completion Day"}
                        <ChevronDown />
                      </Button>
                    }
                  >
                    {({ isOpen, closePortal }) =>
                      isOpen && (
                        <StyledDropdownContent>
                          {dayOfWeekOptions.map(option => (
                            <StyledDropdownButton
                              key={option.label}
                              onClick={() => {
                                requestAnimationFrame(() => {
                                  closePortal();
                                  onDayOfWeekClick(
                                    option.value ? [option.value] : [],
                                  );
                                });
                              }}
                            >
                              {option.label}
                            </StyledDropdownButton>
                          ))}
                        </StyledDropdownContent>
                      )
                    }
                  </StyledDropdown>
                </>
              )}
              <Link
                onClick={() =>
                  requestAnimationFrame(() => {
                    onChange(null);
                    closeMainPortal();
                  })
                }
              >
                Clear Repeat
              </Link>
            </StyledMainDropdownContent>
          )
        );
      }}
    </Dropdown>
  );
};
